import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Dropdown,
  Form,
  Image,
  Input,
  List,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  EllipsisOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileOutlined,
} from '@ant-design/icons';
import { trpc } from '../lib/api';
import { FileUploader } from '../components/file/FileUploader';
import { ACCEPTED_DOCUMENT_LABEL, ACCEPTED_DOCUMENT_TYPES } from '../constants/documentAccept';
import { MarkdownDisplay } from '../components/common/MarkdownDisplay';
import { RichTextEditor } from '../components/common/RichTextEditor';
import { LocationTreeSelect } from '../components/common/LocationTreeSelect';
import { BuildingLocationSelect } from '../components/common/BuildingLocationSelect';
import { SingleUserSelect } from '../components/common/SingleUserSelect';
import { withDevModalFilePath } from '../components/common';

const { Title, Text } = Typography;
const { Modal } = withDevModalFilePath('client/src/pages/DocumentDetailPage.tsx');

type DocumentAssociationEntityType = 'location' | 'equipment' | 'part' | 'supplier' | 'user' | 'purchase_order';
type DocumentRelatedEntityType = DocumentAssociationEntityType | 'location_building';

const ROLE_PRIORITY: Record<string, number> = {
  viewer: 0,
  user: 1,
  manager: 2,
  admin: 3,
};

const entityTypeLabels: Record<string, string> = {
  location: 'エリア',
  equipment: '設備',
  part: '部品',
  supplier: '業者',
  user: 'ユーザー',
  purchase_order: '発注',
};

const formatDate = (value?: string | Date | null) => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('ja-JP');
};

const formatFileSize = (bytes?: number | null) => {
  if (!bytes || bytes <= 0) return '-';
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = Math.round((bytes / Math.pow(k, i)) * 100) / 100;
  return `${value} ${sizes[i]}`;
};

const normalizeDocumentFileName = (fileName?: string | null) => {
  const safeName = (fileName ?? '').trim();
  if (!safeName) return '';
  return safeName.toLowerCase();
};

const resolveOfficeKind = (mimeType?: string | null, fileName?: string | null) => {
  const mime = (mimeType ?? '').toLowerCase();
  const name = (fileName ?? '').toLowerCase();
  if (mime.includes('word') || mime.includes('officedocument.wordprocessingml') || name.endsWith('.doc') || name.endsWith('.docx')) {
    return 'word';
  }
  if (mime.includes('excel') || mime.includes('spreadsheet') || mime.includes('officedocument.spreadsheetml') || name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.xlsm') || name.endsWith('.csv')) {
    return 'excel';
  }
  if (mime.includes('powerpoint') || mime.includes('presentation') || mime.includes('officedocument.presentationml') || name.endsWith('.ppt') || name.endsWith('.pptx')) {
    return 'powerpoint';
  }
  return null;
};

const resolvePreviewLabel = (mimeType?: string | null, fileName?: string | null) => {
  const mime = (mimeType ?? '').toLowerCase();
  const officeKind = resolveOfficeKind(mimeType, fileName);
  if (officeKind === 'word') return { label: 'WORD', color: '#2b579a', icon: <FileWordOutlined /> };
  if (officeKind === 'excel') return { label: 'EXCEL', color: '#217346', icon: <FileExcelOutlined /> };
  if (officeKind === 'powerpoint') return { label: 'POWERPOINT', color: '#d24726', icon: <FilePptOutlined /> };
  if (mime === 'application/pdf') return { label: 'PDF', color: '#d4380d', icon: <FilePdfOutlined /> };
  return { label: 'FILE', color: '#595959', icon: <FileOutlined /> };
};

const renderHtmlThumbnail = (fileName?: string | null, mimeType?: string | null) => {
  const meta = resolvePreviewLabel(mimeType, fileName);
  const name = fileName ?? 'ファイル';
  return (
    <div
      style={{
        width: 160,
        height: 120,
        borderRadius: 6,
        border: '1px solid #e5e5e5',
        background: '#fafafa',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          background: meta.color,
          color: '#fff',
          padding: '6px 8px',
          fontSize: 12,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {meta.icon}
        {meta.label}
      </div>
      <div style={{ padding: '8px', fontSize: 11, color: '#595959' }}>
        <div style={{ fontWeight: 600, marginBottom: 4, wordBreak: 'break-all' }}>{name}</div>
      </div>
    </div>
  );
};

type DocumentFileRow = {
  id: string;
  name: string;
  role?: string | null;
  displayOrder?: number | null;
  currentVersionId?: string | null;
};

type DocumentFileVersionRow = {
  documentFileVersion: {
    id: string;
    documentFileId: string;
    versionNumber: number;
    fileId: string;
    changesNote?: string | null;
    createdAt?: string | Date | null;
  };
  file?: {
    id: string;
    originalName?: string | null;
    mimeType?: string | null;
    fileSize?: number | null;
  } | null;
  fileVersion?: {
    fileSize?: number | null;
  } | null;
};

type DocumentTypeOption = {
  id: string;
  typeKey?: string | null;
  name: string;
  category: 'general' | 'official';
  isEnabled?: boolean;
  relatedEntityType?: DocumentRelatedEntityType | null;
  readRole?: 'viewer' | 'user' | 'manager' | 'admin' | null;
  writeRole?: 'viewer' | 'user' | 'manager' | 'admin' | null;
};

type EquipmentSummary = { id: string; name?: string | null; displayId?: string | null };
type PartSummary = { id: string; name?: string | null; displayId?: string | null };
type SupplierSummary = { id: string; name?: string | null };

const normalizeItems = <T,>(data?: unknown) => {
  if (!data || typeof data !== 'object') return [];
  const collection = data as { items?: unknown; data?: unknown; users?: unknown };
  if (Array.isArray(collection.items)) return collection.items as T[];
  if (Array.isArray(collection.data)) return collection.data as T[];
  if (Array.isArray(collection.users)) return collection.users as T[];
  return [];
};

type DocumentDetailPageProps = {
  documentId?: string;
  mode?: 'page' | 'modal';
  onClose?: () => void;
};

export const DocumentDetailPage: React.FC<DocumentDetailPageProps> = ({
  documentId,
  mode = 'page',
  onClose,
}) => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  const activeDocumentId = documentId ?? idParam ?? '';
  const utils = trpc.useUtils();

  const { data: profile } = trpc.user.getProfile.useQuery();
  const actorRole = profile?.role ?? 'viewer';
  const canManageUsers = ['admin', 'manager'].includes(profile?.role ?? '');

  const { data, isLoading, error } = trpc.document.get.useQuery(activeDocumentId, {
    enabled: Boolean(activeDocumentId),
  });

  const { data: documentTypes = [], isLoading: isTypesLoading } = trpc.document.listTypes.useQuery();

  const document = data?.document;
  const association = data?.association;
  const documentFiles = useMemo(
    () => (data?.documentFiles ?? []) as DocumentFileRow[],
    [data?.documentFiles]
  );
  const documentFileVersions = useMemo(
    () => (data?.documentFileVersions ?? []) as DocumentFileVersionRow[],
    [data?.documentFileVersions]
  );

  const fileIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of documentFileVersions) {
      if (item.file?.id) {
        ids.add(item.file.id);
      } else if (item.documentFileVersion?.fileId) {
        ids.add(item.documentFileVersion.fileId);
      }
    }
    return Array.from(ids);
  }, [documentFileVersions]);

  const { data: thumbnailUrls } = trpc.file.getDerivativeUrls.useQuery(
    { fileIds, profile: 'thumb_md' },
    { enabled: fileIds.length > 0 }
  );

  const associationType = association?.entityType;
  const associationId = association?.entityId ?? '';

  const { data: associationLocation } = trpc.location.getById.useQuery(
    associationId ? { id: associationId } : undefined,
    { enabled: associationType === 'location' && Boolean(associationId) }
  );
  const { data: associationEquipment } = trpc.equipment.getById.useQuery(
    associationId ? { id: associationId } : undefined,
    { enabled: associationType === 'equipment' && Boolean(associationId) }
  );
  const { data: associationPart } = trpc.parts.getById.useQuery(
    associationId ? { id: associationId } : undefined,
    { enabled: associationType === 'part' && Boolean(associationId) }
  );
  const { data: associationSupplier } = trpc.suppliers.getById.useQuery(
    associationId ? { id: associationId } : undefined,
    { enabled: associationType === 'supplier' && Boolean(associationId) }
  );
  const { data: associationUser } = trpc.user.getById.useQuery(
    associationType === 'user' ? associationId : '',
    { enabled: associationType === 'user' && Boolean(associationId) }
  );
  const { data: associationPurchaseOrder } = trpc.purchaseOrders.getById.useQuery(
    associationId ? { id: associationId } : undefined,
    { enabled: associationType === 'purchase_order' && Boolean(associationId) }
  );

  const associationName = useMemo(() => {
    if (!associationType) return null;
    if (associationType === 'location') {
      return associationLocation?.name ?? null;
    }
    if (associationType === 'equipment') {
      const name = associationEquipment?.name ?? null;
      const displayId = associationEquipment?.displayId ?? null;
      return name && displayId ? `${name} (${displayId})` : name;
    }
    if (associationType === 'part') {
      const name = associationPart?.name ?? null;
      const displayId = associationPart?.displayId ?? null;
      return name && displayId ? `${name} (${displayId})` : name;
    }
    if (associationType === 'supplier') {
      return associationSupplier?.name ?? null;
    }
    if (associationType === 'user') {
      return associationUser?.name ?? associationUser?.email ?? null;
    }
    if (associationType === 'purchase_order') {
      return associationPurchaseOrder?.order?.displayId ?? null;
    }
    return null;
  }, [
    associationType,
    associationLocation,
    associationEquipment,
    associationPart,
    associationSupplier,
    associationUser,
    associationPurchaseOrder,
  ]);

  const documentTypeName = useMemo(() => {
    if (!document) return '-';
    return documentTypes.find((type) => type.id === document.documentTypeId)?.name ?? '-';
  }, [document, documentTypes]);

  const versionsByFileId = useMemo(() => {
    const map = new Map<string, DocumentFileVersionRow[]>();
    for (const item of documentFileVersions) {
      const documentFileId = item.documentFileVersion?.documentFileId;
      if (!documentFileId) continue;
      const list = map.get(documentFileId) ?? [];
      list.push(item);
      map.set(documentFileId, list);
    }
    for (const [key, list] of map.entries()) {
      list.sort((a, b) => (b.documentFileVersion.versionNumber ?? 0) - (a.documentFileVersion.versionNumber ?? 0));
      map.set(key, list);
    }
    return map;
  }, [documentFileVersions]);

  const existingFileNames = useMemo(() => {
    return documentFiles.map((docFile) => {
      const versions = versionsByFileId.get(docFile.id) ?? [];
      const current = versions.find(
        (item) => item.documentFileVersion.id === docFile.currentVersionId
      ) ?? versions[0];
      return current?.file?.originalName ?? docFile.name;
    });
  }, [documentFiles, versionsByFileId]);

  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [selectedDocumentFile, setSelectedDocumentFile] = useState<DocumentFileRow | null>(null);
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
  const [newFileModalOpen, setNewFileModalOpen] = useState(false);
  const [newUploadedFileId, setNewUploadedFileId] = useState<string | null>(null);
  const [expandedFileIds, setExpandedFileIds] = useState<Record<string, boolean>>({});
  const [versionForm] = Form.useForm();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm] = Form.useForm();
  const canUploadVersion = Boolean(association?.entityType && association?.entityId);

  const typedDocumentTypes = documentTypes as DocumentTypeOption[];
  const hasMinimumRole = React.useCallback((role: string, minimum: string) =>
    (ROLE_PRIORITY[role] ?? 0) >= (ROLE_PRIORITY[minimum] ?? 0), []);
  const canEdit = useMemo(() => {
    const currentType = typedDocumentTypes.find((type) => type.id === document?.documentTypeId);
    const requiredWriteRole = currentType?.writeRole ?? 'user';
    return hasMinimumRole(actorRole, requiredWriteRole);
  }, [actorRole, document?.documentTypeId, hasMinimumRole, typedDocumentTypes]);
  const editDocumentTypeId = Form.useWatch('documentTypeId', editForm) as string | undefined;
  const editEntityType = Form.useWatch('entityType', editForm) as DocumentAssociationEntityType | undefined;
  const selectedEditDocumentType = useMemo(
    () => typedDocumentTypes.find((type) => type.id === editDocumentTypeId) ?? null,
    [typedDocumentTypes, editDocumentTypeId]
  );
  const fixedEditRelatedEntityType = selectedEditDocumentType?.relatedEntityType ?? null;
  const fixedEditAssociationType = fixedEditRelatedEntityType === 'location_building'
    ? 'location'
    : fixedEditRelatedEntityType;
  const useBuildingList = fixedEditRelatedEntityType === 'location_building'
    || (selectedEditDocumentType?.typeKey
      ? ['statutory_special_building_survey', 'statutory_building_equipment_inspection', 'statutory_fire_protection_inspection']
        .includes(selectedEditDocumentType.typeKey)
      : false);
  const editTypeOptions = useMemo(() => {
    if (!document) return [];
    const currentTypeId = document.documentTypeId;
    return typedDocumentTypes
      .filter((type) => hasMinimumRole(actorRole, type.writeRole ?? 'user'))
      .map((type) => {
        const disabledByEnable = type.isEnabled === false && type.id !== currentTypeId;
        const disabledByUnsupportedAssociation =
          type.relatedEntityType === 'purchase_order'
          && association?.entityType !== 'purchase_order'
          && type.id !== currentTypeId;
        return {
          label: type.name,
          value: type.id,
          disabled: disabledByEnable || disabledByUnsupportedAssociation,
        };
      });
  }, [typedDocumentTypes, document, actorRole, hasMinimumRole, association?.entityType]);

  const { data: equipmentData } = trpc.equipment.list.useQuery(
    { page: 1, limit: 200 },
    { enabled: editModalOpen && editEntityType === 'equipment' },
  );
  const { data: partData } = trpc.parts.list.useQuery(
    { page: 1, limit: 200 },
    { enabled: editModalOpen && editEntityType === 'part' },
  );
  const { data: supplierData } = trpc.suppliers.list.useQuery(
    { page: 1, limit: 200 },
    { enabled: editModalOpen && editEntityType === 'supplier' },
  );

  const equipmentOptions = useMemo(() => {
    const items = normalizeItems<EquipmentSummary>(equipmentData);
    return items.map((item) => ({
      label: item.displayId ? `${item.name ?? item.id} (${item.displayId})` : (item.name ?? item.id),
      value: item.id,
    }));
  }, [equipmentData]);

  const partOptions = useMemo(() => {
    const items = normalizeItems<PartSummary>(partData);
    return items.map((item) => ({
      label: item.displayId ? `${item.name ?? item.id} (${item.displayId})` : (item.name ?? item.id),
      value: item.id,
    }));
  }, [partData]);

  const supplierOptions = useMemo(() => {
    const items = normalizeItems<SupplierSummary>(supplierData);
    return items.map((item) => ({
      label: item.name ?? item.id,
      value: item.id,
    }));
  }, [supplierData]);

  const addVersionMutation = trpc.document.addFileVersion.useMutation({
    onSuccess: async () => {
      message.success('新しいバージョンを登録しました');
      setVersionModalOpen(false);
      setUploadedFileId(null);
      setSelectedDocumentFile(null);
      versionForm.resetFields();
      if (activeDocumentId) {
        await utils.document.get.invalidate(activeDocumentId);
      }
    },
    onError: (error) => {
      message.error(error.message || 'バージョン登録に失敗しました');
    },
  });

  const addFileMutation = trpc.document.addFile.useMutation({
    onSuccess: async () => {
      message.success('ファイルを追加しました');
      setNewFileModalOpen(false);
      setNewUploadedFileId(null);
      if (activeDocumentId) {
        await utils.document.get.invalidate(activeDocumentId);
      }
    },
    onError: (error) => {
      message.error(error.message || 'ファイル追加に失敗しました');
    },
  });

  const deleteMutation = trpc.document.delete.useMutation({
    onSuccess: async () => {
      message.success('書類を削除しました');
      await utils.document.list.invalidate();
      if (mode === 'modal') {
        onClose?.();
        return;
      }
      navigate('/app/documents');
    },
    onError: (error) => {
      message.error(error.message || '書類削除に失敗しました');
    },
  });

  const updateMutation = trpc.document.update.useMutation({
    onSuccess: async () => {
      message.success('書類情報を更新しました');
      setEditModalOpen(false);
      editForm.resetFields();
      if (activeDocumentId) {
        await utils.document.get.invalidate(activeDocumentId);
      }
      await utils.document.list.invalidate();
    },
    onError: (error) => {
      message.error(error.message || '書類更新に失敗しました');
    },
  });

  useEffect(() => {
    if (!editModalOpen) return;
    if (fixedEditAssociationType && editEntityType !== fixedEditAssociationType) {
      editForm.setFieldsValue({ entityType: fixedEditAssociationType, entityId: undefined });
    }
  }, [editModalOpen, fixedEditAssociationType, editEntityType, editForm]);

  useEffect(() => {
    if (!editModalOpen) return;
    if (useBuildingList && editEntityType === 'location') {
      editForm.setFieldsValue({ entityId: undefined });
    }
  }, [editModalOpen, useBuildingList, editEntityType, editForm]);

  const handleAddVersion = async () => {
    if (!selectedDocumentFile?.id) return;
    const values = await versionForm.validateFields();
    if (!uploadedFileId) {
      message.warning('ファイルをアップロードしてください');
      return;
    }
    addVersionMutation.mutate({
      documentFileId: selectedDocumentFile.id,
      fileId: uploadedFileId,
      changesNote: values.changesNote || undefined,
    });
  };

  const handleAddFile = async () => {
    if (!document?.id) return;
    if (!newUploadedFileId) {
      message.warning('ファイルをアップロードしてください');
      return;
    }
    addFileMutation.mutate({
      documentId: document.id,
      fileId: newUploadedFileId,
    });
  };

  const handleDownload = async (fileId: string, fileName?: string | null) => {
    try {
      const { url } = await utils.file.getDownloadUrl.fetch({ fileId, action: 'download' });
      const link = window.document.createElement('a');
      link.href = url;
      if (fileName) {
        link.download = fileName;
      }
      link.click();
    } catch {
      message.error('ダウンロードに失敗しました');
    }
  };

  const handleOpenVersionModal = (file: DocumentFileRow) => {
    setSelectedDocumentFile(file);
    setUploadedFileId(null);
    versionForm.resetFields();
    setVersionModalOpen(true);
  };

  const handleOpenEditModal = () => {
    if (!document) return;
    editForm.setFieldsValue({
      title: document.title,
      description: document.description ?? '',
      documentTypeId: document.documentTypeId,
      entityType: association?.entityType ?? undefined,
      entityId: association?.entityId ?? undefined,
    });
    setEditModalOpen(true);
  };

  const handleUpdateDocument = async () => {
    const values = await editForm.validateFields();
    if (!document) return;
    updateMutation.mutate({
      documentId: document.id,
      title: values.title,
      description: values.description ?? null,
      documentTypeId: values.documentTypeId,
      association: values.entityType && values.entityId
        ? { entityType: values.entityType, entityId: values.entityId }
        : undefined,
    });
  };

  const entityTypeOptions = useMemo(() => {
    const base = [
      { label: 'エリア', value: 'location' },
      { label: '設備', value: 'equipment' },
      { label: '部品', value: 'part' },
      { label: '業者', value: 'supplier' },
    ];
    const canShowUser = canManageUsers || association?.entityType === 'user' || fixedEditRelatedEntityType === 'user';
    const withUser = canShowUser
      ? [...base, { label: 'ユーザー', value: 'user' }]
      : base;
    const canShowPurchaseOrder =
      association?.entityType === 'purchase_order'
      || fixedEditRelatedEntityType === 'purchase_order';
    return canShowPurchaseOrder
      ? [...withUser, { label: '発注', value: 'purchase_order' }]
      : withUser;
  }, [canManageUsers, association?.entityType, fixedEditRelatedEntityType]);

  const renderEditEntitySelector = () => {
    if (!editEntityType) {
      return <Text type="secondary">関連タイプを選択してください</Text>;
    }

    switch (editEntityType) {
      case 'location':
        return useBuildingList
          ? <BuildingLocationSelect placeholder="建物タイプのエリアを選択" />
          : <LocationTreeSelect placeholder="エリアを選択" />;
      case 'equipment':
        return (
          <Select
            showSearch
            allowClear
            placeholder="設備を選択"
            options={equipmentOptions}
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      case 'part':
        return (
          <Select
            showSearch
            allowClear
            placeholder="部品・消耗品を選択"
            options={partOptions}
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      case 'supplier':
        return (
          <Select
            showSearch
            allowClear
            placeholder="業者を選択"
            options={supplierOptions}
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      case 'user':
        return <SingleUserSelect showAssignMeButton />;
      case 'purchase_order':
        return <Input readOnly />;
      default:
        return null;
    }
  };

  const handleDeleteDocument = () => {
    if (!document) return;
    Modal.confirm({
      title: '書類を削除しますか？',
      content: '削除すると一覧から非表示になります。',
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: () => deleteMutation.mutate({ documentId: document.id }),
    });
  };

  const actionMenuItems = [
    {
      key: 'edit',
      label: '編集',
      icon: <EditOutlined />,
      onClick: handleOpenEditModal,
    },
    {
      key: 'delete',
      label: '削除',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleDeleteDocument,
    },
  ];

  const toggleHistory = (fileId: string) => {
    setExpandedFileIds((prev) => ({
      ...prev,
      [fileId]: !prev[fileId],
    }));
  };

  if (!activeDocumentId) {
    return (
      <Card style={{ margin: 24 }}>
        <Text>書類IDが指定されていません。</Text>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !document) {
    return (
      <Card style={{ margin: 24 }}>
        <Text>書類情報の取得に失敗しました。</Text>
      </Card>
    );
  }

  const associationLabel = association?.entityType
    ? entityTypeLabels[association.entityType] ?? association.entityType
    : '-';

  const associationLink = () => {
    if (!association?.entityType || !association?.entityId) return null;
    if (association.entityType === 'location') {
      return (
        <Button type="link" onClick={() => navigate(`/app/locations#${association.entityId}`)}>
          エリアを開く
        </Button>
      );
    }
    if (association.entityType === 'equipment') {
      return (
        <Button type="link" onClick={() => navigate(`/app/equipments/${association.entityId}`)}>
          設備を開く
        </Button>
      );
    }
    if (association.entityType === 'part') {
      return (
        <Button type="link" onClick={() => navigate(`/app/parts/${association.entityId}`)}>
          部品を開く
        </Button>
      );
    }
    if (association.entityType === 'supplier') {
      return (
        <Button type="link" onClick={() => navigate(`/app/suppliers/${association.entityId}`)}>
          業者を開く
        </Button>
      );
    }
    if (association.entityType === 'purchase_order') {
      return (
        <Button type="link" onClick={() => navigate(`/app/purchase-orders/${association.entityId}`)}>
          発注を開く
        </Button>
      );
    }
    return null;
  };

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => {
                if (mode === 'modal') {
                  onClose?.();
                  return;
                }
                navigate('/app/documents');
              }}
            >
              {mode === 'modal' ? '閉じる' : '書類へ戻る'}
            </Button>
            <Title level={3} style={{ margin: 0 }}>{document.title}</Title>
            <Tag color={document.category === 'official' ? 'red' : 'default'}>
              {document.category === 'official' ? '法定点検' : '一般'}
            </Tag>
          </Space>
          {canEdit && (
            <Dropdown
              menu={{ items: actionMenuItems }}
              trigger={['click']}
            >
              <Button icon={<EllipsisOutlined />}>
                操作
              </Button>
            </Dropdown>
          )}
        </Space>

        <Card title="書類情報">
          <Descriptions column={2} bordered>
            <Descriptions.Item label="書類名" span={2}>
              {document.title}
            </Descriptions.Item>
            <Descriptions.Item label="書類カテゴリ">
              {documentTypeName}
            </Descriptions.Item>
            <Descriptions.Item label="ファイル数">
              {documentFiles.length}
            </Descriptions.Item>
            <Descriptions.Item label="関連">
              <Space size="small">
                <Tag>{associationLabel}</Tag>
                {association?.entityId ? (
                  <Text type="secondary">{associationName ?? association.entityId}</Text>
                ) : (
                  <Text type="secondary">-</Text>
                )}
                {associationLink()}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="更新日">
              {formatDate(document.updatedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="作成日">
              {formatDate(document.createdAt)}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="説明">
          {document.description ? (
            <MarkdownDisplay content={document.description} />
          ) : (
            <Text type="secondary">説明は未登録です。</Text>
          )}
        </Card>

        <Card
          title="ファイル一覧"
          extra={canEdit && canUploadVersion ? (
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() => {
                setNewFileModalOpen(true);
                setNewUploadedFileId(null);
              }}
            >
              ファイル追加
            </Button>
          ) : null}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {documentFiles.length === 0 ? (
              <Text type="secondary">ファイルが登録されていません。</Text>
            ) : (
              documentFiles.map((docFile) => {
                const versions = versionsByFileId.get(docFile.id) ?? [];
                const currentVersion = versions.find(
                  (item) => item.documentFileVersion.id === docFile.currentVersionId
                ) ?? versions[0] ?? null;
                const currentFileId = currentVersion?.file?.id ?? currentVersion?.documentFileVersion.fileId ?? null;
                const currentPreviewUrl = currentFileId ? thumbnailUrls?.[currentFileId] ?? null : null;
                const currentFileName = currentVersion?.file?.originalName ?? docFile.name;
                const currentMimeType = currentVersion?.file?.mimeType ?? null;
                const currentFileSize = formatFileSize(
                  currentVersion?.fileVersion?.fileSize ?? currentVersion?.file?.fileSize ?? null
                );
                const currentVersionLabel = currentVersion
                  ? `v${currentVersion.documentFileVersion.versionNumber}`
                  : '-';
                const roleLabel = docFile.role ?? 'source';

                return (
                  <Card
                    key={docFile.id}
                    size="small"
                    type="inner"
                    title={
                      <Space size="small">
                        <Text strong>{docFile.name}</Text>
                        <Tag>{roleLabel}</Tag>
                        <Tag color="blue">{currentVersionLabel}</Tag>
                      </Space>
                    }
                    extra={canEdit && canUploadVersion ? (
                      <Button size="small" icon={<UploadOutlined />} onClick={() => handleOpenVersionModal(docFile)}>
                        改版
                      </Button>
                    ) : null}
                  >
                    <Row gutter={[16, 16]}>
                      <Col flex="160px">
                        {currentPreviewUrl ? (
                          <Image
                            src={currentPreviewUrl}
                            width={160}
                            height={120}
                            style={{ objectFit: 'cover', borderRadius: 6 }}
                            preview={false}
                          />
                        ) : (
                          renderHtmlThumbnail(currentFileName, currentMimeType)
                        )}
                      </Col>
                      <Col flex="auto">
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Text strong style={{ wordBreak: 'break-all' }}>{currentFileName}</Text>
                          <Text type="secondary">
                            {currentFileSize} ・ 最終更新: {formatDate(currentVersion?.documentFileVersion.createdAt)}
                          </Text>
                          {currentVersion?.documentFileVersion.changesNote && (
                            <Text type="secondary">変更メモ: {currentVersion.documentFileVersion.changesNote}</Text>
                          )}
                          {currentFileId && (
                            <Button
                              size="small"
                              icon={<DownloadOutlined />}
                              onClick={() => handleDownload(currentFileId, currentFileName)}
                            >
                              最新ファイルをダウンロード
                            </Button>
                          )}
                        </Space>
                      </Col>
                    </Row>

                    <Space direction="vertical" size="small" style={{ width: '100%', marginTop: 16 }}>
                      <Space size="small" align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                        <Text type="secondary">バージョン履歴</Text>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => toggleHistory(docFile.id)}
                        >
                          {expandedFileIds[docFile.id] ? '履歴を隠す' : '履歴を表示'}
                        </Button>
                      </Space>
                      {expandedFileIds[docFile.id] && (
                        <List
                          size="small"
                          dataSource={versions}
                          locale={{ emptyText: 'バージョンはまだ登録されていません。' }}
                          renderItem={(row) => {
                            const fileId = row.file?.id ?? row.documentFileVersion.fileId;
                            const fileName = row.file?.originalName ?? docFile.name;
                            const fileSize = formatFileSize(
                              row.fileVersion?.fileSize ?? row.file?.fileSize ?? null
                            );
                            const versionLabel = `v${row.documentFileVersion.versionNumber}`;
                            const isCurrent = row.documentFileVersion.id === docFile.currentVersionId;

                            return (
                              <List.Item
                                actions={[
                                  <Button
                                    key="download"
                                    size="small"
                                    icon={<DownloadOutlined />}
                                    onClick={() => handleDownload(fileId, row.file?.originalName ?? fileName)}
                                  >
                                    ダウンロード
                                  </Button>,
                                ]}
                              >
                                <List.Item.Meta
                                  title={(
                                    <Space size="small" wrap>
                                      <Tag color={isCurrent ? 'blue' : 'default'}>{versionLabel}</Tag>
                                      {isCurrent && <Tag color="green">最新</Tag>}
                                      <Text strong style={{ wordBreak: 'break-all' }}>{fileName}</Text>
                                    </Space>
                                  )}
                                  description={(
                                    <Space direction="vertical" size={0}>
                                      <Text type="secondary" style={{ fontSize: 12 }}>
                                        {fileSize} ・ {formatDate(row.documentFileVersion.createdAt)}
                                      </Text>
                                      {row.documentFileVersion.changesNote && (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                          {row.documentFileVersion.changesNote}
                                        </Text>
                                      )}
                                    </Space>
                                  )}
                                />
                              </List.Item>
                            );
                          }}
                        />
                      )}
                    </Space>
                  </Card>
                );
              })
            )}
          </Space>
        </Card>
      </Space>

      <Modal
        title="書類編集"
        open={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          editForm.resetFields();
        }}
        onOk={() => void handleUpdateDocument()}
        okText="保存"
        cancelText="キャンセル"
        confirmLoading={updateMutation.isPending}
        destroyOnHidden
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="title"
            label="書類名"
            rules={[{ required: true, message: '書類名を入力してください' }]}
          >
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item
            name="description"
            label="説明"
            valuePropName="value"
            trigger="onChange"
          >
            <RichTextEditor placeholder="書類の詳細や補足を記載" minHeight={160} />
          </Form.Item>
          <Form.Item
            name="documentTypeId"
            label="書類カテゴリ"
            rules={[{ required: true, message: '書類カテゴリを選択してください' }]}
          >
            <Select options={editTypeOptions} loading={isTypesLoading} />
          </Form.Item>
          <Form.Item
            name="entityType"
            label="関連タイプ"
            rules={[{ required: true, message: '関連タイプを選択してください' }]}
          >
            <Select options={entityTypeOptions} disabled={Boolean(fixedEditRelatedEntityType)} />
          </Form.Item>
          <Form.Item
            name="entityId"
            label="関連先"
            rules={[{ required: true, message: '関連先を選択してください' }]}
          >
            {renderEditEntitySelector()}
          </Form.Item>
        </Form>
        {editTypeOptions.length === 0 && (
          <Text type="secondary">利用可能な書類カテゴリがありません。</Text>
        )}
      </Modal>

      <Modal
        title="改版アップロード"
        open={versionModalOpen}
        onCancel={() => {
          setVersionModalOpen(false);
          setUploadedFileId(null);
          setSelectedDocumentFile(null);
          versionForm.resetFields();
        }}
        onOk={() => void handleAddVersion()}
        okText="登録"
        cancelText="キャンセル"
        confirmLoading={addVersionMutation.isPending}
        okButtonProps={{ disabled: !canUploadVersion || !selectedDocumentFile }}
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {selectedDocumentFile && (
            <Text>対象ファイル: {selectedDocumentFile.name}</Text>
          )}
          <Form layout="vertical" form={versionForm}>
            <Form.Item name="changesNote" label="変更メモ">
              <Input.TextArea rows={3} maxLength={500} placeholder="改版内容を簡単に記載" />
            </Form.Item>
          </Form>
          <Text type="secondary">アップロード済み: {uploadedFileId ? 1 : 0}件</Text>
          {canUploadVersion ? (
            <FileUploader
              entityType={association!.entityType}
              entityId={association!.entityId}
              category="document"
              accept={ACCEPTED_DOCUMENT_TYPES}
              acceptLabel={ACCEPTED_DOCUMENT_LABEL}
              maxFiles={1}
              maxSize={25}
              onUploadSuccess={(file) => {
                if (file?.id) {
                  setUploadedFileId(file.id);
                }
              }}
            />
          ) : (
            <Text type="secondary">関連付け情報が不足しているためアップロードできません。</Text>
          )}
        </Space>
      </Modal>

      <Modal
        title="ファイル追加"
        open={newFileModalOpen}
        onCancel={() => {
          setNewFileModalOpen(false);
          setNewUploadedFileId(null);
        }}
        onOk={() => void handleAddFile()}
        okText="追加"
        cancelText="キャンセル"
        confirmLoading={addFileMutation.isPending}
        okButtonProps={{ disabled: !canUploadVersion }}
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text type="secondary">同名ファイルは追加できません。改版は「改版」から行ってください。</Text>
          <Text type="secondary">アップロード済み: {newUploadedFileId ? 1 : 0}件</Text>
          {canUploadVersion ? (
            <FileUploader
              entityType={association!.entityType}
              entityId={association!.entityId}
              category="document"
              accept={ACCEPTED_DOCUMENT_TYPES}
              acceptLabel={ACCEPTED_DOCUMENT_LABEL}
              maxFiles={1}
              maxSize={25}
              disallowDuplicateNames
              existingFileNames={existingFileNames}
              normalizeDuplicateName={normalizeDocumentFileName}
              onUploadSuccess={(file) => {
                if (file?.id) {
                  setNewUploadedFileId(file.id);
                }
              }}
            />
          ) : (
            <Text type="secondary">関連付け情報が不足しているためアップロードできません。</Text>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default DocumentDetailPage;
